import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { RefreshCw, X } from 'lucide-react';

const PERIODS = [
  { label: 'Сегодня', value: '1d'  },
  { label: '7 дней',  value: '7d'  },
  { label: '30 дней', value: '30d' },
  { label: '90 дней', value: '90d' },
];

const SOURCE_LABELS = {
  phone:    'С телефона',
  phone_tg: 'С Telegram',
  usb:      'USB флешка',
  scan:     'Сканер / Копия',
};

const fmt = (n) => new Intl.NumberFormat('ru-RU').format(n || 0);

function BarChart({ title, data, dataKey, max, color }) {
  const scrollRef = useRef(null);

  // Минимальная ширина столбика. Если столбиков много и суммарно они шире
  // карточки — включается горизонтальная прокрутка; на коротком периоде
  // flex-grow растягивает их на всю доступную ширину.
  const BAR_MIN  = 22;
  const GAP      = 3;
  const count    = data?.length || 0;
  const rowWidth = count * BAR_MIN + Math.max(0, count - 1) * GAP;

  // При обновлении данных прокручиваем к правому краю — там самые свежие дни.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
  }, [data]);

  return (
    <div className="card" style={{ padding: '20px' }}>
      <p style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '16px' }}>
        {title}
      </p>
      {data?.length > 0 ? (
        <div ref={scrollRef} style={{ overflowX: 'auto', overflowY: 'hidden', paddingBottom: '6px' }}>
          <div style={{
            display: 'flex', gap: `${GAP}px`, alignItems: 'flex-end',
            height: '120px', width: `${rowWidth}px`, minWidth: '100%',
          }}>
            {data.map((d, i) => {
              const pct = max > 0 ? d[dataKey] / max : 0;
              return (
                <div
                  key={i}
                  title={`${d.day}: ${fmt(d[dataKey])}`}
                  style={{ flex: 1, minWidth: `${BAR_MIN}px`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', gap: '4px', paddingBottom: '16px' }}
                >
                  <div style={{
                    width: '100%',
                    background: color,
                    borderRadius: '3px 3px 0 0',
                    height: `${Math.max(2, pct * 80)}px`,
                    opacity: 0.75,
                    transition: 'height 0.4s var(--ease)',
                  }} />
                  <span style={{ fontSize: '9px', color: 'var(--text-3)', transform: 'rotate(-45deg)', transformOrigin: 'center', whiteSpace: 'nowrap' }}>
                    {d.day?.slice(5)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="empty" style={{ padding: '24px 0', fontSize: '12px' }}>Нет данных</div>
      )}
    </div>
  );
}

function groupBySourceMode(rows = []) {
  const result = {};
  for (const row of rows) {
    const src  = row.source  || 'phone';
    const mode = row.is_duplex ? 'duplex' : 'simplex';
    if (!result[src]) result[src] = {};
    result[src][mode] = { orders: row.orders, sheets: row.sheets };
  }
  return result;
}

export const Stats = () => {
  const { fetchAuthorized, isAdmin } = useAuth();
  const [period, setPeriod]     = useState('7d');
  const [deviceId, setDeviceId] = useState('');   // '' = все аппараты
  const [dateFrom, setDateFrom] = useState('');   // кастомный диапазон: дата с
  const [dateTo, setDateTo]     = useState('');   // кастомный диапазон: дата по
  const [devices, setDevices]   = useState([]);
  const [profiles, setProfiles] = useState([]);   // для фильтра по профилю (admin)
  const [profileFilter, setProfileFilter] = useState('all'); // all | none | <id>
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);

  // Загружаем список устройств для фильтра (+ профили для админа)
  useEffect(() => {
    fetchAuthorized('/api/dashboard/devices')
      .then(r => r.json())
      .then(d => { if (d.success) setDevices(d.devices); })
      .catch(() => {});
    if (isAdmin) {
      fetchAuthorized('/api/dashboard/profiles')
        .then(r => r.json())
        .then(d => { if (d.success) setProfiles(d.profiles); })
        .catch(() => {});
    }
  }, [fetchAuthorized, isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Если заданы обе даты — шлём кастомный диапазон, иначе пресет period.
      const qs = new URLSearchParams();
      if (dateFrom && dateTo) {
        qs.set('date_from', dateFrom);
        qs.set('date_to', dateTo);
      } else {
        qs.set('period', period);
      }
      if (deviceId) qs.set('device_id', deviceId);
      if (isAdmin && profileFilter !== 'all') qs.set('profile_filter', profileFilter);
      const res = await fetchAuthorized(`/api/dashboard/stats?${qs.toString()}`);
      const j   = await res.json();
      if (j.success) setData(j);
    } catch {}
    finally { setLoading(false); }
  }, [fetchAuthorized, period, deviceId, dateFrom, dateTo, isAdmin, profileFilter]);

  useEffect(() => { load(); }, [load]);

  // Выбор пресета сбрасывает кастомный диапазон дат.
  const selectPeriod = (value) => {
    setDateFrom('');
    setDateTo('');
    setPeriod(value);
  };

  // Кастомный диапазон активен только когда заданы ОБЕ даты.
  const customRange   = Boolean(dateFrom && dateTo);
  // Сегодня по календарю Ташкента (UTC+5) — верхняя граница для полей дат.
  const todayTashkent = new Date(Date.now() + 5 * 3600 * 1000).toISOString().slice(0, 10);
  // Дата первого заказа (приходит с сервера в /stats, учитывает выбранный
  // аппарат) — нижняя граница: раньше неё аппарат ещё не работал.
  const minDate = data?.first_order_date || undefined;

  const t    = data?.totals;
  const maxR = data?.daily?.reduce((m, d) => Math.max(m, d.revenue), 0) || 1;
  const maxS = data?.daily?.reduce((m, d) => Math.max(m, d.sheets),  0) || 1;
  const bySourceMode = groupBySourceMode(data?.by_source_mode);

  const selectedDevice     = devices.find(d => d.device_id === deviceId);
  const selectedDeviceName = selectedDevice ? (selectedDevice.name || selectedDevice.machine_name) : null;

  const summary = [
    { label: 'Выручка',           value: `${fmt(t?.total_revenue)} UZS`, color: 'var(--yellow)' },
    { label: 'Оплаченных заказов', value: fmt(t?.paid_orders),            color: 'var(--green)'  },
    { label: 'Листов всего',       value: fmt(t?.total_sheets),           color: null            },
    { label: 'Односторонних л.',   value: fmt(t?.simplex_sheets),         color: null            },
    { label: 'Двусторонних л.',    value: fmt(t?.duplex_sheets),          color: 'var(--brand)'  },
    { label: 'Ошибок',             value: fmt(t?.failed_orders),          color: t?.failed_orders > 0 ? 'var(--red)' : null },
  ];

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Статистика</h1>
          <p>
            {selectedDeviceName ? `Аппарат: ${selectedDeviceName}` : 'Все аппараты'}
            {customRange && ` · период ${dateFrom} — ${dateTo}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Profile filter (admin): считать цифры с профильными киосками или без */}
          {isAdmin && (
            <select
              value={profileFilter}
              onChange={e => setProfileFilter(e.target.value)}
              style={{ height: '32px', fontSize: '13px', borderRadius: '8px', padding: '0 10px', background: 'var(--bg-card)', color: 'var(--text-1)', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer' }}
            >
              <option value="all">Все киоски</option>
              <option value="none">Только без профиля</option>
              {profiles.map(p => (
                <option key={p.id} value={String(p.id)}>
                  👤 {p.display_name || p.username}
                </option>
              ))}
            </select>
          )}

          {/* Device filter */}
          <select
            value={deviceId}
            onChange={e => setDeviceId(e.target.value)}
            style={{ height: '32px', fontSize: '13px', borderRadius: '8px', padding: '0 10px', background: 'var(--bg-card)', color: 'var(--text-1)', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer' }}
          >
            <option value="">Все аппараты</option>
            {devices.map(d => (
              <option key={d.device_id} value={d.device_id}>
                {d.name || d.machine_name || d.device_id.slice(0, 14)}
              </option>
            ))}
          </select>

          {/* Period filter */}
          {PERIODS.map(p => (
            <button
              key={p.value}
              onClick={() => selectPeriod(p.value)}
              className={`btn${period === p.value && !customRange ? ' btn-primary' : ''}`}
              style={{ padding: '6px 12px' }}
            >
              {p.label}
            </button>
          ))}

          {/* Custom date range filter */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '5px',
            paddingLeft: '8px', borderLeft: '1px solid rgba(255,255,255,0.12)',
          }}>
            <input
              type="date"
              value={dateFrom}
              min={minDate}
              max={dateTo || todayTashkent}
              onChange={e => setDateFrom(e.target.value)}
              title={minDate ? `Дата начала периода (не раньше ${minDate} — первый заказ)` : 'Дата начала периода'}
              style={{
                height: '32px', fontSize: '13px', borderRadius: '8px', padding: '0 8px',
                background: 'var(--bg-card)', color: 'var(--text-1)', cursor: 'pointer',
                colorScheme: 'dark',
                border: `1px solid ${customRange ? 'var(--brand)' : 'rgba(255,255,255,0.1)'}`,
              }}
            />
            <span style={{ color: 'var(--text-3)', fontSize: '13px' }}>—</span>
            <input
              type="date"
              value={dateTo}
              min={dateFrom || minDate}
              max={todayTashkent}
              onChange={e => setDateTo(e.target.value)}
              title={minDate ? `Дата конца периода (не раньше ${minDate} — первый заказ)` : 'Дата конца периода'}
              style={{
                height: '32px', fontSize: '13px', borderRadius: '8px', padding: '0 8px',
                background: 'var(--bg-card)', color: 'var(--text-1)', cursor: 'pointer',
                colorScheme: 'dark',
                border: `1px solid ${customRange ? 'var(--brand)' : 'rgba(255,255,255,0.1)'}`,
              }}
            />
            {(dateFrom || dateTo) && (
              <button
                className="btn-icon"
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                title="Сбросить диапазон дат"
                style={{ width: '28px', height: '28px' }}
              >
                <X size={13} />
              </button>
            )}
          </div>

          <button className="btn-icon" onClick={load} disabled={loading} title="Обновить" style={{ marginLeft: '2px' }}>
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="stats-summary-grid">
        {summary.map(({ label, value, color }) => (
          <div key={label} className="card stat-card">
            <div className="stat-value" style={{ fontSize: '18px', ...(color ? { color } : {}) }}>
              {value ?? '—'}
            </div>
            <div className="stat-label">{label}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="stats-charts-grid">
        <BarChart title="Выручка по дням (UZS)" data={data?.daily} dataKey="revenue" max={maxR} color="var(--yellow)" />
        <BarChart title="Листов по дням"         data={data?.daily} dataKey="sheets"  max={maxS} color="var(--brand)"  />
      </div>

      {/* By source & mode breakdown */}
      <div className="card" style={{ overflow: 'hidden', marginBottom: '14px' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', fontWeight: '600', fontSize: '13px' }}>
          По разделам и режиму · {selectedDeviceName || 'Все аппараты'}
        </div>
        <table>
          <thead>
            <tr>
              <th>Раздел</th>
              <th>Режим</th>
              <th>Заказов</th>
              <th>Листов</th>
            </tr>
          </thead>
          <tbody>
            {data?.by_source_mode?.length > 0 ? (
              Object.entries(SOURCE_LABELS).flatMap(([src, srcLabel]) => {
                const modes = bySourceMode[src];
                if (!modes) return [];
                return [
                  modes.simplex && (
                    <tr key={`${src}-s`}>
                      <td><span style={{ fontWeight: '600', fontSize: '13px' }}>{srcLabel}</span></td>
                      <td><span style={{ fontSize: '12px', color: 'var(--text-2)' }}>Одностороннее</span></td>
                      <td className="text-muted">{fmt(modes.simplex.orders)}</td>
                      <td style={{ fontWeight: '600' }}>{fmt(modes.simplex.sheets)} л.</td>
                    </tr>
                  ),
                  modes.duplex && (
                    <tr key={`${src}-d`}>
                      <td>{!modes.simplex && <span style={{ fontWeight: '600', fontSize: '13px' }}>{srcLabel}</span>}</td>
                      <td><span style={{ fontSize: '12px', color: 'var(--brand)' }}>Двустороннее</span></td>
                      <td className="text-muted">{fmt(modes.duplex.orders)}</td>
                      <td style={{ fontWeight: '600' }}>{fmt(modes.duplex.sheets)} л.</td>
                    </tr>
                  ),
                ].filter(Boolean);
              })
            ) : (
              <tr>
                <td colSpan={4} className="empty">
                  {loading ? 'Загрузка...' : 'Нет оплаченных заказов за период'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Per-device breakdown (only in all-devices mode) */}
      {!deviceId && (
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', fontWeight: '600', fontSize: '13px' }}>
            По аппаратам
          </div>
          <table>
            <thead>
              <tr>
                <th>Устройство</th>
                <th>Выручка</th>
                <th>Заказы</th>
                <th>Листов</th>
                <th style={{ width: '160px' }}>Доля</th>
              </tr>
            </thead>
            <tbody>
              {data?.by_device?.length > 0 ? data.by_device.map(d => {
                const maxDev = data.by_device.reduce((m, x) => Math.max(m, x.revenue), 0) || 1;
                const pct = Math.min(100, (d.revenue / maxDev) * 100);
                return (
                  <tr key={d.device_id} style={{ cursor: 'pointer' }} onClick={() => setDeviceId(d.device_id)}>
                    <td>
                      <div style={{ fontWeight: '600', fontSize: '13px' }}>
                        {d.name || d.machine_name || 'Unnamed'}
                        {isAdmin && d.profile_id && (
                          <span style={{
                            fontSize: '10px', padding: '1px 7px', borderRadius: '20px', marginLeft: '6px',
                            background: 'rgba(129,140,248,0.14)', color: 'var(--brand)', fontWeight: 600,
                          }}>
                            👤 {d.profile_display_name || d.profile_username}
                          </span>
                        )}
                      </div>
                      <div className="text-dim mono" style={{ fontSize: '11px', marginTop: '1px' }}>{d.device_id?.slice(0, 12)}…</div>
                    </td>
                    <td>
                      <span style={{ fontWeight: '600' }}>{fmt(d.revenue)}</span>
                      <span className="text-dim" style={{ fontSize: '11px' }}> UZS</span>
                    </td>
                    <td className="text-muted">{fmt(d.orders)}</td>
                    <td className="text-muted">{fmt(d.sheets)} л.</td>
                    <td>
                      <div style={{ height: '5px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: 'var(--brand)', borderRadius: '3px', transition: 'width 0.4s var(--ease)' }} />
                      </div>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={5} className="empty">
                    {loading ? 'Загрузка...' : 'Нет данных за период'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
